<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type PropType
} from 'vue';
import Drawer from 'primevue/drawer';
import { DateTime } from 'luxon';
import { CustomButton, ButtonType } from '@/components/common/';
import { useCaseNoteStore } from '@/stores/caseNote';
import { parseTimepointToMinutes } from '@/utils';
import type {
  CaseNoteModel,
  TimepointOption
} from '@/models/caseNote';
import CaseNoteDialog from './CaseNoteDialog.vue';
import SortBar from './SortBar.vue';
import NoteList from './NoteList.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  caseIdentifier: {
    type: String,
    required: true
  },
  caseLabel: {
    type: String,
    default: ''
  },
  timepointOptions: {
    type: Array as PropType<TimepointOption[]>,
    default: () => []
  },
  showPinButton: {
    type: Boolean,
    default: false
  },
  inline: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['onClose', 'onAddNote', 'onPinChange']);

const caseNoteStore = useCaseNoteStore();
const panelInstanceId = `panel-${Math.random().toString(36).slice(2)}`;

const editingNote = ref<CaseNoteModel | undefined>();
const showEditDialog = ref(false);
const isPinned = ref(false);
const isMobile = ref(window.matchMedia('(max-width: 768px)').matches);
const activeCaseIdentifier = ref(props.caseIdentifier);
const activeCaseLabel = ref(props.caseLabel);

const updateIsMobile = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches;
};

onMounted(() => {
  window.addEventListener('resize', updateIsMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
  caseNoteStore.unregisterPinnedPanel(panelInstanceId);
});

const panelTitle = computed(() => {
  return activeCaseLabel.value ? `Notes - ${activeCaseLabel.value}` : 'Case Notes';
});

const DATE_TIME_24H_FORMAT = 'dd MMM yyyy, HH:mm';

const perfusionStartTimeText = computed(() => {
  const nmpStartTime = caseNoteStore.nmpStartTime;
  if (!nmpStartTime) {
    return '';
  }

  const parsed = DateTime.fromISO(nmpStartTime, { setZone: true });
  return parsed.isValid ? parsed.toFormat(DATE_TIME_24H_FORMAT) : '';
});

const showPinControl = computed(() => props.showPinButton && !isMobile.value);

const drawerStyle = computed(() => ({
  width: 'min(420px, 80vw)'
}));

watch(
  () => props.visible,
  isVisible => {
    if (isVisible && activeCaseIdentifier.value) {
      caseNoteStore.getNotes(activeCaseIdentifier.value);
    } else {
      caseNoteStore.unregisterPinnedPanel(panelInstanceId);
      isPinned.value = false;
      caseNoteStore.clearNotes();
    }
  }
);

watch(
  () => props.caseIdentifier,
  caseIdentifier => {
    if (isPinned.value) {
      return;
    }

    activeCaseIdentifier.value = caseIdentifier;
  }
);

watch(
  () => props.caseLabel,
  caseLabel => {
    if (isPinned.value) {
      return;
    }

    activeCaseLabel.value = caseLabel;
  }
);

watch(
  () => props.showPinButton,
  enabled => {
    if (!enabled) {
      isPinned.value = false;
    }
  }
);

const handleClose = () => {
  caseNoteStore.unregisterPinnedPanel(panelInstanceId);
  isPinned.value = false;
  emit('onPinChange', false);
  emit('onClose');
};

const togglePin = () => {
  isPinned.value = !isPinned.value;
  if (isPinned.value) {
    caseNoteStore.registerPinnedPanel(
      panelInstanceId,
      activeCaseIdentifier.value,
      activeCaseLabel.value
    );
  } else {
    caseNoteStore.unregisterPinnedPanel(panelInstanceId);
  }
  emit('onPinChange', isPinned.value);
};

const openEditDialog = (note: CaseNoteModel) => {
  editingNote.value = note;
  showEditDialog.value = true;
};

const closeEditDialog = () => {
  showEditDialog.value = false;
  editingNote.value = undefined;
};

const onEditSaved = async () => {
  closeEditDialog();
  if (props.visible && activeCaseIdentifier.value) {
    await caseNoteStore.getNotes(activeCaseIdentifier.value);
  }
};

watch(
  () => caseNoteStore.pinnedPanelRequestVersion,
  async () => {
    if (!isPinned.value) {
      return;
    }

    if (caseNoteStore.pinnedPanelOwnerId !== panelInstanceId) {
      return;
    }

    const nextCaseId = caseNoteStore.pinnedPanelCaseId;
    if (!nextCaseId) {
      return;
    }

    activeCaseIdentifier.value = nextCaseId;
    activeCaseLabel.value = caseNoteStore.pinnedPanelCaseLabel;
    await caseNoteStore.getNotes(nextCaseId);
  }
);

// ── Sorting ──────────────────────────────────────────────────────────────────

type SortOption = 'createdAt' | 'eventTime';
type SortDir = 'desc' | 'asc';
const sortOption = ref<SortOption>('eventTime');
const sortDir = ref<SortDir>('desc');

const selectSort = (option: SortOption) => {
  if (sortOption.value === option) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc';
  } else {
    sortOption.value = option;
    sortDir.value = 'desc';
  }
};

const compareTimepointValues = (
  a: string,
  b: string,
  dir: SortDir
): number => {
  const aValue = parseTimepointToMinutes(a);
  const bValue = parseTimepointToMinutes(b);
  const aFinite = Number.isFinite(aValue);
  const bFinite = Number.isFinite(bValue);

  // Always keep invalid/unparsable timepoints at the end, regardless of sort direction.
  if (aFinite !== bFinite) {
    return aFinite ? -1 : 1;
  }

  if (!aFinite && !bFinite) {
    return 0;
  }

  if (dir === 'desc') {
    return bValue - aValue;
  }

  return aValue - bValue;
};

const sortedNotes = computed(() => {
  const notes = [...caseNoteStore.notes];
  const dir = sortDir.value === 'desc' ? 1 : -1;

  if (sortOption.value === 'createdAt') {
    return notes.sort(
      (a, b) => dir * (b.createdAt.getTime() - a.createdAt.getTime())
    );
  }

  const withTime = notes.filter(n => !!n.eventTime);
  const withTp = notes.filter(n => !n.eventTime && !!n.timepoint);
  const rest = notes.filter(n => !n.eventTime && !n.timepoint);
  withTime.sort(
    (a, b) => dir * (b.eventTime!.getTime() - a.eventTime!.getTime())
  );
  withTp.sort((a, b) => {
    const byTimepoint = compareTimepointValues(
      a.timepoint!,
      b.timepoint!,
      sortDir.value
    );
    if (byTimepoint !== 0) {
      return byTimepoint;
    }

    return dir * (b.createdAt.getTime() - a.createdAt.getTime());
  });
  rest.sort((a, b) => dir * (b.createdAt.getTime() - a.createdAt.getTime()));
  return [...withTime, ...withTp, ...rest];
});
</script>

<template>
  <!-- Inline docked mode: rendered directly in parent container -->
  <template v-if="inline && visible">
    <div class="notes-inline-header">
      <div class="notes-header-text">
        <span class="notes-title">{{ panelTitle }}</span>
        <span class="notes-subtitle" v-if="perfusionStartTimeText">
          Perfusion start time: {{ perfusionStartTimeText }}
        </span>
      </div>
      <CustomButton :type="ButtonType.ICON_ONLY"
        :icon="{ icon: 'xmark', style: 'fas' }"
        tooltip="Close notes"
        @onClick="handleClose"/>
    </div>
    <div class="panel-actions">
      <CustomButton text="Add Note"
        :type="ButtonType.POSITIVE"
        :icon="{ icon: 'plus', style: 'fas' }"
        @onClick="emit('onAddNote')"/>
    </div>
    <SortBar :sort-option="sortOption"
      :sort-dir="sortDir"
      v-if="caseNoteStore.notes.length > 1"
      @select="selectSort"/>
    <NoteList :notes="sortedNotes"
      :loading="caseNoteStore.notesLoader.isLoading"
      @edit="openEditDialog"/>
  </template>

  <Drawer v-else-if="visible && !isPinned"
    :visible="visible"
    position="right"
    :header="panelTitle"
    class="notes-drawer"
    :style="drawerStyle"
    @update:visible="handleClose">
    <div class="notes-subtitle notes-drawer-subtitle" v-if="perfusionStartTimeText">
      Perfusion start time: {{ perfusionStartTimeText }}
    </div>
    <div class="panel-actions">
      <CustomButton text="Add Note"
        :type="ButtonType.POSITIVE"
        :icon="{ icon: 'plus', style: 'fas' }"
        @onClick="emit('onAddNote')"/>
      <CustomButton v-if="showPinControl"
        :text="isPinned ? 'Unpin' : 'Pin'"
        :type="ButtonType.OUTLINED"
        :icon="{ icon: 'thumbtack', style: 'fas' }"
        @onClick="togglePin"/>
    </div>
    <SortBar :sort-option="sortOption"
      :sort-dir="sortDir"
      v-if="caseNoteStore.notes.length > 1"
      @select="selectSort"/>
    <NoteList :notes="sortedNotes"
      :loading="caseNoteStore.notesLoader.isLoading"
      @edit="openEditDialog"/>
  </Drawer>

  <div class="notes-pinned-shell" v-else-if="visible">
    <div class="notes-pinned-panel">
      <div class="notes-pinned-header">
        <div class="notes-header-text">
          <div class="notes-title">{{ panelTitle }}</div>
          <div class="notes-subtitle" v-if="perfusionStartTimeText">
            Perfusion start time: {{ perfusionStartTimeText }}
          </div>
        </div>
        <CustomButton :type="ButtonType.ICON_ONLY"
          :icon="{ icon: 'xmark', style: 'far' }"
          tooltip="Close notes"
          @onClick="handleClose"/>
      </div>

      <div class="panel-actions">
        <CustomButton text="Add Note"
          :type="ButtonType.POSITIVE"
          :icon="{ icon: 'plus', style: 'fas' }"
          @onClick="emit('onAddNote')"/>
        <CustomButton v-if="showPinControl"
          text="Unpin"
          :type="ButtonType.OUTLINED"
          :icon="{ icon: 'thumbtack', style: 'fas' }"
          @onClick="togglePin"/>
      </div>
      <SortBar :sort-option="sortOption"
        :sort-dir="sortDir"
        v-if="caseNoteStore.notes.length > 1"
        @select="selectSort"/>
      <NoteList :notes="sortedNotes"
        :loading="caseNoteStore.notesLoader.isLoading"
        @edit="openEditDialog"/>
    </div>
  </div>

  <CaseNoteDialog :visible="showEditDialog"
    :case-identifier="activeCaseIdentifier"
    :existing-note="editingNote"
    :timepoint-options="timepointOptions"
    @onClose="closeEditDialog"
    @onSaved="onEditSaved"/>
</template>

<style scoped lang="scss">
@use "../../../assets/_variables.module.scss" as *;

.notes-inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.notes-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.panel-actions {
  padding: 0 0 16px;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.notes-pinned-shell {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 98;
  width: min(428px, 80vw);
  border-left: 1px solid var(--color-border);
  background: #fff;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  padding: 16px;
  padding-top: 72px;
  overflow: hidden;
}

.notes-pinned-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.notes-pinned-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 12px;
  margin-bottom: 12px;
}

.notes-title {
  font-size: 16px;
  font-weight: 700;
}

.notes-subtitle {
  font-size: 12px;
  line-height: 1.4;
  color: #6b7280;
}

.notes-drawer-subtitle {
  margin: -4px 0 12px;
}

.notes-list-container {
  flex: 1;
  overflow-y: auto;
  padding-top: 16px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 160px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(0, 0, 0, 0.4);
  text-align: center;
  gap: 8px;

  .empty-icon {
    font-size: 40px;
    opacity: 0.4;
  }

  p {
    font-size: 15px;
    font-weight: 600;
    margin: 0;
  }

  span {
    font-size: 13px;
  }
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.note-card {
  background: #f8f9fc;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 12px 14px;
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    border-color: var(--p-primary-color, #{$primary-color});
    box-shadow:
      0 0 0 3px rgba(0, 102, 255, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08);

    .note-actions {
      opacity: 1;
    }
  }
}

.note-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.note-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.note-timepoint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: $primary-color;
  background: rgba(0, 102, 255, 0.08);
  padding: 2px 8px;
  border-radius: 12px;

  .timepoint-icon {
    font-size: 10px;
  }
}

.note-content {
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text);
  margin: 0 0 10px;
  white-space: pre-wrap;
  word-break: break-word;
}

.note-history {
  border-top: 1px dashed var(--color-border);
  margin-top: 8px;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

</style>
